import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** Tipos y llamadas de la pestaña «Archivos del día» (propios, para no tocar odoo.service.ts). */
export type ClaseArchivo = 'creditos' | 'pagos';

export interface HistorialDia {
  accion: 'subido' | 'regenerado' | 'error';
  fecha: string;
  usuario: string | null;
  origen: string;
  motivo: string | null;
  error: string | null;
  filas: number | null;
  clase?: ClaseArchivo;
}

export interface ArchivoDia {
  _id: string;
  fecha_operacion: string;
  clase: ClaseArchivo;
  nombre: string;
  filas: number;
  monto_total_usd: number;
  comercios?: number | null;
  estado: 'subido' | 'error' | 'anotado' | 'con_problemas';
  error: string | null;
  intentos?: number;
  subido_en?: string | null;
  updatedAt?: string;
}

export interface DiaArchivos { fecha: string; creditos: ArchivoDia | null; pagos: ArchivoDia | null; }

export interface DiaFaltante extends DiaArchivos {
  motivo: 'error' | 'sin_archivo' | 'incompleto';
  clases: ClaseArchivo[];
  error: string | null;
  intentos: number;
}

export interface ListaDias {
  dias: DiaArchivos[];
  total: number;
  page: number;
  limit: number;
  faltantes: DiaFaltante[];
  proxima_subida: { fecha: string; hora: number } | null;
  hora_subida: number;
  subida_activa: boolean;
}

export interface ComercioTotal { comercio: string; monto: number; filas: number; creditos: number; }
export interface TotalesArchivo { filas: number; monto: number; creditos: number; comercios: ComercioTotal[]; }

export interface DetalleDia {
  fecha: string;
  origen: 'buzon' | 'previa';
  clase: ClaseArchivo;
  existe: boolean;
  registros: { creditos: ArchivoDia | null; pagos: ArchivoDia | null };
  totales: { creditos: TotalesArchivo | null; pagos: TotalesArchivo | null };
  encabezados: string[];
  items: Array<Array<string | number>>;
  total: number;
  page: number;
  limit: number;
  historial: HistorialDia[];
}

export interface FiltroDetalle { clase: ClaseArchivo; q: string; comercio: string; page: number; limit: number; }

@Injectable({ providedIn: 'root' })
export class OdooArchivosService {
  private readonly base = `${environment.BASE_LA}/api/odoo/archivos`;
  constructor(private http: HttpClient) {}

  dias(page: number, limit: number): Observable<ListaDias> {
    const params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    return this.http.get<{ data: ListaDias }>(`${this.base}/dias`, { params }).pipe(map((r) => r.data));
  }

  detalle(fecha: string, f: FiltroDetalle, previa: boolean): Observable<DetalleDia> {
    let params = new HttpParams().set('clase', f.clase).set('page', String(f.page)).set('limit', String(f.limit));
    if (f.q.trim()) params = params.set('q', f.q.trim());
    if (f.comercio) params = params.set('comercio', f.comercio);
    return this.http.get<{ data: DetalleDia }>(`${this.base}/dias/${fecha}${previa ? '/previa' : ''}`, { params }).pipe(map((r) => r.data));
  }

  /** Genera y sube los 2 archivos de un día que aún no se subió («Subir ahora» / «Subir al buzón»). */
  subir(fecha: string): Observable<ArchivoDia[]> {
    return this.http.post<{ data: ArchivoDia[] }>(`${this.base}/generar`, { fecha }).pipe(map((r) => r.data));
  }

  regenerar(fecha: string, motivo: string): Observable<ArchivoDia[]> {
    return this.http.post<{ data: ArchivoDia[] }>(`${this.base}/regenerar`, { fecha, motivo }).pipe(map((r) => r.data));
  }

  descargar(id: string): Observable<Blob> {
    return this.http.get(`${this.base}/${encodeURIComponent(id)}/descargar`, { responseType: 'blob' });
  }
}

// ── Formato de fechas (hora de Venezuela) ──
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

function partes(ymd: string): { d: string; m: string; y: string; sem: string } {
  const [y, m, d] = ymd.slice(0, 10).split('-');
  const sem = DIAS[new Date(`${y}-${m}-${d}T00:00:00Z`).getUTCDay()];
  return { d, m: MESES[Number(m) - 1] || m, y, sem };
}
/** «Sábado 17-oct-2026» */
export function diaLargo(ymd: string): string { const p = partes(ymd); return `${cap(p.sem)} ${p.d}-${p.m}-${p.y}`; }
/** «martes 20-oct» */
export function diaCorto(ymd: string): string { const p = partes(ymd); return `${p.sem} ${p.d}-${p.m}`; }
/** «martes 06-oct» (sin año, para títulos del diálogo) */
export const diaMedio = diaCorto;

const fHora = new Intl.DateTimeFormat('es-VE', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Caracas' });
const fYmd = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas', year: 'numeric', month: '2-digit', day: '2-digit' });
/** «07-oct, 1:02 a. m.» */
export function momento(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = partes(fYmd.format(d));
  return `${p.d}-${p.m}, ${fHora.format(d)}`;
}
/** «07-oct a la 1:02 a. m.» */
export function momentoLargo(iso: string | null | undefined): string {
  const t = momento(iso);
  if (!t) return '';
  const [dia, hora] = t.split(', ');
  return `${dia} a ${/^1:/.test(hora) ? 'la' : 'las'} ${hora}`;
}
/** «1:00 a. m.» para una hora entera 0-23 */
export function horaEntera(h: number): string {
  const n = ((Math.trunc(h) % 24) + 24) % 24;
  return `${n % 12 === 0 ? 12 : n % 12}:00 ${n < 12 ? 'a. m.' : 'p. m.'}`;
}
export function diaVE(dias = 0): string { return fYmd.format(new Date(Date.now() + dias * 86400000)); }

const fMonto = new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** «28,21 $» */
export const usd = (n: number | null | undefined): string => (typeof n === 'number' ? `${fMonto.format(n)} $` : '—');
export const num = (n: number | null | undefined): string => (typeof n === 'number' ? new Intl.NumberFormat('es-VE').format(n) : '—');
